// TreeNode — stateless SVG card for one person in the top-down org tree.
//
// Layout (inside the card's local coordinate space — caller positions the
// `<g>` via the `x`/`y` props):
//
//   ┌──────────────────────────────────────────┐
//   │  ⬤  Display Name                     [+N] │
//   │     Job Title                             │
//   └──────────────────────────────────────────┘
//
//   - rounded outer rect with a team-coloured border
//   - leading circle filled with `color`, containing white initials
//   - two stacked text lines on the right: display name (bold) + job title
//   - optional `+N` badge in the top-right corner when children are hidden
//   - optional "You" badge / halo when this is the signed-in viewer
//
// Two click zones (resolves the US2-vs-US4 conflict from PR #634):
//   - The `+N` badge — its own focusable element with onClick = onToggle.
//     Stops propagation so clicking the badge doesn't also fire onSelect.
//   - The rest of the card — onClick = onSelect (opens the drawer).
//
// Keyboard: Enter / Space on the card invokes `onSelect` when supplied,
// falling back to `onToggle`. The badge has its own Enter / Space handler.

import type { KeyboardEvent, MouseEvent } from 'react';
import type { IOrgGraphNode } from '@skystone/shared';
import { initialsOf, truncateLabel } from '../org-graph/utils';
import { NODE_HEIGHT, NODE_WIDTH } from './hooks/useTreeLayout';

const CIRCLE_RADIUS = 18;
const CIRCLE_CENTER_X = 24;
const CIRCLE_CENTER_Y = NODE_HEIGHT / 2;
const TEXT_LEFT = CIRCLE_CENTER_X + CIRCLE_RADIUS + 10;
const NAME_TEXT_Y = NODE_HEIGHT / 2 - 4;
const TITLE_TEXT_Y = NODE_HEIGHT / 2 + 14;
const BORDER_WIDTH = 2;
const RECT_RADIUS = 8;
const BORDER_OPACITY = 0.45;
/** Truncate the visible name + title at this many graphemes — narrower than
 *  the force-graph default because the card has less horizontal room. */
const MAX_TITLE_CHARS = 22;
/** Hidden-children badge sizing. 18px circle anchored to the top-right. */
const BADGE_RADIUS = 9;
const BADGE_CENTER_X = NODE_WIDTH - BADGE_RADIUS - 2;
const BADGE_CENTER_Y = BADGE_RADIUS + 2;
/** Square hitbox around the badge — needs to be large enough to land a mouse
 *  click reliably but not so large that it overlaps the rest of the card. */
const BADGE_HITBOX = BADGE_RADIUS * 2 + 4;

/** Halo (current-user marker) sits 4 logical units outside the card on each
 *  side and uses a 2-unit primary-coloured stroke. */
const HALO_INSET = 4;
const HALO_STROKE_WIDTH = 2;
/** "You" badge: a small pill in the top-right corner of the card, 32x16.
 *  Distinct constants from the hidden-children circle badge above to avoid
 *  a naming collision after the merge with US2. */
const YOU_BADGE_WIDTH = 32;
const YOU_BADGE_HEIGHT = 16;
const YOU_BADGE_RIGHT_PADDING = 6;
const YOU_BADGE_TOP_PADDING = 6;
const YOU_BADGE_CORNER_RADIUS = 8;
const YOU_BADGE_FONT_SIZE = 10;

interface TreeNodeProps {
  /** The person rendered by this card. */
  node: IOrgGraphNode;
  /** Top-left x of the card (logical SVG units). */
  x: number;
  /** Top-left y of the card (logical SVG units). */
  y: number;
  /** Team colour for the initials circle + border. */
  color: string;
  /** Number of direct children currently hidden under this node. When
   *  greater than 0, a `+N` badge renders in the top-right corner. */
  hiddenCount?: number;
  /** Click handler for the `+N` badge (collapse toggle). */
  onToggle?: () => void;
  /** When true, render a transient CSS pulse-ring around the card. The
   *  consumer is responsible for clearing this back to `false` after the
   *  animation completes (OrgTree wires this via a `setTimeout`). */
  isHighlighted?: boolean;
  /** Optional key-down handler for arrow-key navigation. Wired by OrgTree's
   *  `useTreeKeyboardNav` so the focused card consumes Arrow* and Space.
   *  Card click itself opens the `PersonHoverCard` popover that wraps the
   *  card (PRODOPSAI-497) — no `onSelect` is needed here. */
  onKeyDown?: (event: KeyboardEvent<SVGGElement>) => void;
  /** Fired when the card receives DOM focus (Tab-into, or arrow-key
   *  navigation has just moved here). Lets OrgTree update its
   *  `focusedNodeId` state so subsequent keypresses use the right source. */
  onFocus?: (id: string) => void;
  /** ARIA depth from root, 1-indexed. Omit on the virtual root (never
   *  reachable — it's filtered out before rendering anyway). */
  ariaLevel?: number;
  /** Whether this card has at least one child (visible or hidden). When
   *  true, the card carries `aria-expanded`. Leaves omit the attribute
   *  entirely, per ARIA tree-pattern spec. */
  hasChildren?: boolean;
  /** True when this card's subtree is currently collapsed (i.e. hidden). */
  isCollapsed?: boolean;
  /** When `true`, the card is the keyboard-focus target (`tabIndex=0` and
   *  `data-focused="true"` are emitted). All other cards render with
   *  `tabIndex=-1` so Tab lands exactly once inside the tree, on the
   *  user's initial card. Undefined keeps the legacy `tabIndex=0`-on-
   *  every-card behaviour for tests that pre-date the focused-id model. */
  focused?: boolean;
}

/** Pulse-ring used when the consumer marks this card as `isHighlighted`.
 *  Renders as an outset rect with no fill so the pulse is visible around
 *  the card. Distinct from the current-user halo so the two can co-exist. */
const HIGHLIGHT_INSET = 6;
const HIGHLIGHT_STROKE_WIDTH = 3;

export function TreeNode({
  node,
  x,
  y,
  color,
  hiddenCount,
  onToggle,
  isHighlighted,
  onKeyDown,
  onFocus,
  ariaLevel,
  hasChildren,
  isCollapsed,
  focused,
}: Readonly<TreeNodeProps>) {
  const initials = initialsOf(node);
  const visibleName = truncateLabel(node.displayName, MAX_TITLE_CHARS);
  const visibleTitle = node.jobTitle === null ? null : truncateLabel(node.jobTitle, MAX_TITLE_CHARS);
  const hasHidden = hiddenCount !== undefined && hiddenCount > 0;
  // Card is "interactive" (focusable / takes a tabIndex / has a button-ish
  // role) whenever the card is wired into a navigable tree — i.e. OrgTree
  // supplied keyboard, focus, or toggle handlers. The card itself no longer
  // has an onClick: clicks bubble to the outer `<g>` that the wrapping
  // `PersonHoverCard` registers as its trigger (PRODOPSAI-497), so a click
  // opens the popover instead of firing a card-local handler.
  const interactive =
    onToggle !== undefined || onKeyDown !== undefined || onFocus !== undefined;
  const handleCardKeyDown = (event: KeyboardEvent<SVGGElement>) => {
    if (onKeyDown !== undefined) {
      onKeyDown(event);
    }
  };
  const handleFocus = () => {
    if (onFocus !== undefined) onFocus(node.id);
  };

  const badgeInteractive = hasHidden && onToggle !== undefined;
  const handleBadgeClick = (event: MouseEvent<SVGGElement>) => {
    if (onToggle === undefined) return;
    event.stopPropagation();
    onToggle();
  };
  const handleBadgeKeyDown = (event: KeyboardEvent<SVGGElement>) => {
    if (onToggle === undefined) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onToggle();
    }
  };

  // Clicking the card opens the wrapping `PersonHoverCard` popover; reflect
  // that as the accessible label. Standalone renders (no handlers wired) get
  // no aria-label since the card has no interaction.
  const ariaLabel = `${node.displayName}, open details`;

  const isCurrentUser = node.isCurrentUser;
  const youBadgeX = NODE_WIDTH - YOU_BADGE_WIDTH - YOU_BADGE_RIGHT_PADDING;
  const youBadgeY = YOU_BADGE_TOP_PADDING;

  // ARIA role: `treeitem` once we have a depth context (OrgTree wires
  // `ariaLevel`). When the card is rendered standalone (unit tests, ad-hoc
  // sandboxes), fall back to the legacy `button` role so the existing
  // assertions keep passing.
  const ariaRole = (() => {
    if (!interactive) return undefined;
    return ariaLevel === undefined ? 'button' : 'treeitem';
  })();
  // tabIndex: when `focused` is supplied (OrgTree-managed roving focus),
  // exactly the focused card carries `0` and the rest carry `-1`. When
  // undefined (legacy callers), every interactive card stays Tab-reachable
  // for backwards compatibility.
  const cardTabIndex = (() => {
    if (!interactive) return undefined;
    if (focused === undefined) return 0;
    return focused ? 0 : -1;
  })();
  // aria-expanded is set ONLY on cards that have children — leaves omit it
  // entirely per the WAI-ARIA tree pattern.
  const ariaExpanded = hasChildren === true ? isCollapsed !== true : undefined;
  // Compose the className. Always include `tree-node-anim` so the wrapping
  // `<g>` transform transitions smoothly on collapse/expand; the rest is
  // gated on interactivity.
  const className = interactive
    ? 'tree-node-anim tree-node-interactive cursor-pointer'
    : 'tree-node-anim';

  return (
    <g
      data-testid="tree-node"
      data-upn={node.id}
      data-focused={focused === true ? 'true' : undefined}
      transform={`translate(${x}, ${y})`}
      role={ariaRole}
      tabIndex={cardTabIndex}
      aria-label={interactive ? ariaLabel : undefined}
      aria-level={ariaLevel}
      aria-expanded={ariaExpanded}
      onKeyDown={interactive ? handleCardKeyDown : undefined}
      onFocus={interactive ? handleFocus : undefined}
      className={className}
    >
      <title>{node.displayName}</title>
      {isHighlighted === true && (
        <rect
          data-testid="tree-node-highlight"
          className="tree-node-highlighted"
          x={-HIGHLIGHT_INSET}
          y={-HIGHLIGHT_INSET}
          width={NODE_WIDTH + HIGHLIGHT_INSET * 2}
          height={NODE_HEIGHT + HIGHLIGHT_INSET * 2}
          rx={RECT_RADIUS + HIGHLIGHT_INSET}
          ry={RECT_RADIUS + HIGHLIGHT_INSET}
          fill="none"
          stroke="var(--color-ring)"
          strokeWidth={HIGHLIGHT_STROKE_WIDTH}
          pointerEvents="none"
        />
      )}
      {isCurrentUser && (
        <rect
          data-testid="tree-node-halo"
          x={-HALO_INSET}
          y={-HALO_INSET}
          width={NODE_WIDTH + HALO_INSET * 2}
          height={NODE_HEIGHT + HALO_INSET * 2}
          rx={RECT_RADIUS + HALO_INSET}
          ry={RECT_RADIUS + HALO_INSET}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth={HALO_STROKE_WIDTH}
        />
      )}
      <rect
        x={0}
        y={0}
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx={RECT_RADIUS}
        ry={RECT_RADIUS}
        fill="var(--color-background)"
        stroke={color}
        strokeOpacity={BORDER_OPACITY}
        strokeWidth={BORDER_WIDTH}
      />
      <circle
        cx={CIRCLE_CENTER_X}
        cy={CIRCLE_CENTER_Y}
        r={CIRCLE_RADIUS}
        fill={color}
      />
      <text
        x={CIRCLE_CENTER_X}
        y={CIRCLE_CENTER_Y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
        fill="white"
        aria-hidden="true"
      >
        {initials}
      </text>
      <text
        x={TEXT_LEFT}
        y={NAME_TEXT_Y}
        fontSize={13}
        fontWeight={600}
        fill="var(--color-foreground)"
      >
        {visibleName}
      </text>
      {visibleTitle !== null && (
        <text
          x={TEXT_LEFT}
          y={TITLE_TEXT_Y}
          fontSize={11}
          fill="var(--color-muted-foreground)"
        >
          {visibleTitle}
        </text>
      )}
      {isCurrentUser && (
        <g data-testid="tree-node-you-badge">
          <rect
            x={youBadgeX}
            y={youBadgeY}
            width={YOU_BADGE_WIDTH}
            height={YOU_BADGE_HEIGHT}
            rx={YOU_BADGE_CORNER_RADIUS}
            ry={YOU_BADGE_CORNER_RADIUS}
            fill="var(--color-primary)"
          />
          <text
            x={youBadgeX + YOU_BADGE_WIDTH / 2}
            y={youBadgeY + YOU_BADGE_HEIGHT / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={YOU_BADGE_FONT_SIZE}
            fontWeight={600}
            fill="var(--color-primary-foreground)"
          >
            You
          </text>
        </g>
      )}
      {hasHidden && (
        <g
          data-testid="tree-node-hidden-badge"
          role={badgeInteractive ? 'button' : undefined}
          tabIndex={badgeInteractive ? 0 : undefined}
          aria-label={badgeInteractive ? `Show ${hiddenCount} hidden direct reports` : undefined}
          onClick={badgeInteractive ? handleBadgeClick : undefined}
          onKeyDown={badgeInteractive ? handleBadgeKeyDown : undefined}
          className={badgeInteractive ? 'tree-node-interactive cursor-pointer' : undefined}
        >
          {/* Invisible square hitbox so the badge is reliably clickable. */}
          <rect
            x={BADGE_CENTER_X - BADGE_HITBOX / 2}
            y={BADGE_CENTER_Y - BADGE_HITBOX / 2}
            width={BADGE_HITBOX}
            height={BADGE_HITBOX}
            fill="transparent"
          />
          <circle
            cx={BADGE_CENTER_X}
            cy={BADGE_CENTER_Y}
            r={BADGE_RADIUS}
            fill="var(--color-muted)"
          />
          <text
            x={BADGE_CENTER_X}
            y={BADGE_CENTER_Y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fontWeight={600}
            fill="var(--color-muted-foreground)"
            aria-hidden="true"
          >
            {`+${hiddenCount}`}
          </text>
        </g>
      )}
    </g>
  );
}
